"use client";

import { useState, useEffect } from "react";
import { ProcessedProject } from "@/lib/types/github";
import {
  fetchGitHubRepos,
  fetchRepoLanguages,
  findProjectImage,
} from "@/lib/github-service";
import { extractDominantColors } from "@/lib/utils";
import { defaultProjectImages } from "@/lib/constants";
import { getRandomItem } from "@/lib/utils";

interface UseProjectsOptions {
  username: string;
  maxProjects?: number;
  defaultImage?: string;
}

interface UseProjectsReturn {
  projects: ProcessedProject[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useProjects({
  username,
  maxProjects = 6,
}: UseProjectsOptions): UseProjectsReturn {
  const [projects, setProjects] = useState<ProcessedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const repos = await fetchGitHubRepos(username);

      const processedProjects = await Promise.all(
        repos.slice(0, maxProjects).map(async (repo) => {
          // Fetch languages
          const languages = await fetchRepoLanguages(repo.languages_url);

          //TODO: APK(s), .exe files e.t.c
          const executableFiles = [];

          // Find project image
          const imageUrl = await findProjectImage(
            username,
            repo.name,
            repo.homepage,
          );

          // Extract colors from image
          let colors = { primary: "59, 130, 246", secondary: "139, 92, 246" };
          if (imageUrl) {
            try {
              colors = await extractDominantColors(imageUrl);
            } catch (error) {
              console.error("Error extracting colors:", error);
            }
          }

          const hasImage = imageUrl ? true : false;

          return {
            id: repo.id,
            name: repo.name,
            description: repo.description || "No description available",
            repoUrl: repo.html_url,
            projectUrl: repo.homepage,
            languages: languages,
            image: imageUrl || `/images/${getRandomItem(defaultProjectImages)}`,
            hasImage,
            colors,
          };
        }),
      );

      setProjects(processedProjects);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Failed to load projects");
      setError(error);
      console.error("Error loading projects:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, maxProjects]);

  return {
    projects,
    loading,
    error,
    refetch: loadProjects,
  };
}
